<script setup>
  import { defineRule } from 'vee-validate'
  import { required, email as emailRule, min } from '@vee-validate/rules'
  import { ref } from 'vue'
  const dialogRef = ref()

  const localePath = useLocalePath()
  const authStore = useAuthStore()

  defineRule('required', required)
  defineRule('email', emailRule)
  defineRule('min', min)

  const loginSchema = {
    email: 'email',
    code: '',
    remember: ''
  }

  const { defineField, handleSubmit, errors } = useForm({
    validationSchema: loginSchema
  })

  const [email] = defineField('email')
  const [code] = defineField('code')
  const [password] = defineField('password')
  const [remember] = defineField('remember')

  const visible = ref(false)
  const showPassword = ref(false)
  const forgotPasswordRef = ref(null)
  const loading = ref(false)

  function openModal() {
    visible.value = true
  }

  function closeModal() {
    visible.value = false
  }

  function openForgotPasswordModal() {
    closeModal()
    if (forgotPasswordRef.value) {
      forgotPasswordRef.value.openModal()
    }
  }

  // Expose the openModal function so it can be called from other components
  defineExpose({
    openModal,
    closeModal
  })

  const handleLogin = handleSubmit(async (values) => {
    loading.value = true
    try {
      let loginEmail = values.email

      // If code is provided, fetch the email first
      if (values.code && values.code.trim()) {
        loginEmail = await authStore.getEmailByUserCode(values.code.trim())
      }

      // If no email and no code provided, show error
      if (!loginEmail || !loginEmail.trim()) {
        authStore.error = 'Please provide either an email or a user code'
        return
      }

      // Proceed with login using the email
      await authStore.login({
        identifier: loginEmail,
        password: values.password
      })

      // Only close modal if login was successful (no error AND user is authenticated)
      if (!authStore.error && authStore.user && authStore.token) {
        closeModal()
      }
    } catch (error) {
      // Error is already set in the store by getEmailByUserCode or login
      console.error('Login failed:', error)
    } finally {
      loading.value = false
    }
  })
</script>
<template>
  <div>
    <div class="flex items-center pl-2 h-8">
      <div class="w-[22px] h-[22px] bg-white/20 rounded-full items-center justify-center flex -mr-1">
        <font-awesome class="text-xs" :icon="['fas', 'user']" />
      </div>
      <Button
        type="button"
        variant="link"
        aria-haspopup="true"
        aria-controls="language_menu"
        pt:root="!text-xs !text-white hover:!text-sky-100 !h-8 !py-0"
        @click="openModal"
      >
        {{ $t('pages.auth.menu.connect') }}
      </Button>
    </div>
    <Dialog
      ref="dialogRef"
      v-model:visible="visible"
      modal
      :draggable="false"
      :dismissable-mask="true"
      position="top"
      class="w-sm"
      pt:root="overflow-hidden"
      pt:mask="pt-24 backdrop-blur-xs"
      pt:header="border-b border-slate-200"
      pt:footer="bg-slate-100 !p-4 !justify-center"
    >
      <template #header>
        <h3 class="text-xl font-medium leading-6 text-slate-900">
          {{ $t('pages.auth.login.title') }}
        </h3>
      </template>
      <template #closeicon>
        <font-awesome class="fa-fw" :icon="['fas', 'xmark']" />
      </template>
      <form class="space-y-4 mt-4" @submit="handleLogin">
        <div>
          <label for="code" class="block text-sm font-medium text-slate-700 pb-2">
            {{ $t('pages.auth.login.form.user_code') }}
          </label>
          <div class="mt-1">
            <InputText
              id="code"
              v-model="code"
              :invalid="!!errors.code"
              :disabled="loading"
              fluid
              type="text"
              autocomplete="off"
              :placeholder="$t('pages.auth.login.form.user_code_placeholder')"
            />
            <Message severity="error" size="small" variant="simple">{{ errors.code }}</Message>
          </div>
        </div>
        <div>
          <label for="email" class="block text-sm font-medium text-slate-700 pb-2">
            {{ $t('pages.auth.login.form.orEmail') }}
          </label>
          <div class="mt-1">
            <InputText
              id="email"
              v-model="email"
              :invalid="!!errors.email"
              :disabled="loading"
              fluid
              autofocus
              type="email"
              autocomplete="email"
              :placeholder="$t('pages.auth.login.form.email_placeholder')"
            />
            <Message severity="error" size="small" variant="simple">{{ errors.email }}</Message>
          </div>
        </div>
        <div>
          <div class="flex items-center justify-between">
            <label for="password" class="block text-sm font-medium text-slate-700">
              {{ $t('pages.auth.login.form.password') }}
            </label>
            <Button
              type="button"
              variant="text"
              size="small"
              :disabled="loading"
              :label="$t('pages.auth.login.form.forgot')"
              @click="openForgotPasswordModal"
            />
          </div>
          <div class="mt-1">
            <div class="relative">
              <InputText
                id="password"
                v-model="password"
                :invalid="errors.password"
                :disabled="loading"
                fluid
                :type="showPassword ? 'text' : 'password'"
                autocomplete="current-password"
              />
              <button
                type="button"
                :disabled="loading"
                class="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                @click="showPassword = !showPassword"
              >
                <font-awesome v-if="showPassword === false" class="fa-fw" :icon="['fas', 'eye-slash']" />
                <font-awesome v-if="showPassword === true" class="fa-fw" :icon="['fas', 'eye']" />
              </button>
            </div>
            <Message severity="error" size="small" variant="simple">{{ errors.password }}</Message>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <Checkbox v-model="remember" input-id="remember" type="checkbox" :disabled="loading" binary />
          <label for="remember">
            {{ $t('pages.auth.login.form.remember') }}
          </label>
        </div>
        <Message v-if="authStore.error" class="mb-2" severity="error" size="small" variant="error">
          {{ $t('pages.auth.login.error') }}
        </Message>
        <div>
          <Button type="submit" fluid :disabled="loading">
            <font-awesome v-if="loading" class="fa-fw mr-2 fa-spin" :icon="['fas', 'spinner-third']" />
            {{ $t('pages.auth.login.form.submit') }}
          </Button>
        </div>
      </form>
      <template #footer>
        <div class="text-slate-500">
          {{ $t('pages.auth.login.signup.title') }}
          <NuxtLink :to="localePath({ name: 'auth-signup' })" class="text-link" @click="closeModal">
            {{ $t('pages.auth.login.signup.link') }}
          </NuxtLink>
        </div>
      </template>
    </Dialog>

    <!-- Forgot Password Modal -->
    <AuthForgotPassword ref="forgotPasswordRef" />
  </div>
</template>
